import useCommonStore from "@/store/commonStore";
import { sendGTMEvent, } from "@next/third-parties/google";

/**
 * @description GTM Event Handlers
 * @author Anas VP
 * @date Jan 06, 2026
 * @description refer given sheet for more details: https://docs.google.com/spreadsheets/d/1sCHZC8aPHsGzee14x4W6GzK5rSoHMRF8rwx_7zwAH9o/edit?gid=1727552525#gid=1727552525
 */

interface IPageViewPayload {
    page_path: string;
    page_search: string;
    page_url: string;
    [key: string]: string;
}
interface IViewItemPayload {
    item_id?: string,
    item_variant?: string,
    item_name?: string,
    item_brand?: string,
    item_category?: string,
    price?: string | number
    quantity?: string | number
}


export const GTMPageViewHandler = (payload: IPageViewPayload) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'page_view',
        cf_uid: user?.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        ...payload
    });
    return 1;
}
export const GTMCategoryListHandler = (type: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'category_list',
        cf_uid: user?.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        category_type: type,
    });
    return 1;
}
export const GTMCategoryItemsListHandler = (type: string, label: string, value: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'view_item_list',
        cf_uid: user?.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        category_type: type,
        item_list_name: label,
        item_list_id: value
    });
    return 1;
}
export const GTMSelectPaymentModeHandler = (type: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'select_payment_mode',
        cf_uid: user?.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        payment_type: type
    });
    return 1;
}

export const GTMViewItemHandler = ({ curr, value, item }: { curr?: string, value?: string, item?: IViewItemPayload }) => {
    return GTMEcommerceHandler('view_item', {}, {
        currency: curr || "",
        value: value || '',
        items: item ? [item] : [],
    });
}

export const GTMAddToCartHandler = ({ curr, value, items }: { curr?: string, value?: number, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('add_to_cart', {}, {
        currency: curr || "",
        value: value || 0,
        items: items || [],
    });
}
export const GTMRemoveFromCartHandler = ({ curr, value, items }: { curr?: string, value?: number, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('remove_from_cart', {}, {
        currency: curr || "",
        value: value || 0,
        items: items || [],
    });
}
export const GTMViewCartHandler = ({ curr, value, items }: { curr?: string, value?: string, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('user_cart', {}, {
        currency: curr || "",
        value: value || '',
        items: items || [],
    });
}
export const GTMViewOrderSummaryHandler = ({ curr, value, items, coupon }: { curr?: string, value?: string, items?: IViewItemPayload[], coupon?: string }) => {
    return GTMEcommerceHandler('view_cart', {}, {
        currency: curr || "",
        value: value || '',
        items: items || [],
        coupon: coupon || '',
    });
}

export const GTMUpdateOrderSummaryHandler = ({ curr, value, items, coupon }: { curr?: string, value?: number, items?: IViewItemPayload[], coupon?: string }) => {
    return GTMEcommerceHandler('update_order_summary', {}, {
        currency: curr || "",
        value: value || '',
        items: items || [],
        coupon: coupon || '',
    });
}
export const GTMBeginCheckoutHandler = ({ curr, value, items, coupon }: { curr?: string, value?: number, items?: IViewItemPayload[], coupon?: string }) => {
    return GTMEcommerceHandler('begin_checkout', {}, {
        currency: curr || "",
        value: value || '',
        items: items || [],
        coupon: coupon || '',
    });
}

export const GTMPurchaseHandler = ({ curr, value, items, transaction_id, tax, shipping, cod_charge, coupon }: { curr?: string, value?: number, items?: IViewItemPayload[], transaction_id?: string, tax?: number, shipping?: number, cod_charge?: number, coupon?: string }) => {
    return GTMEcommerceHandler('purchase', {}, {
        currency: curr || "",
        value: value || '',
        transaction_id: transaction_id || '',
        tax: tax || 0,
        shipping: shipping || 0,
        cod_charge: cod_charge || 0,
        items: items || [],
        coupon: coupon || '',
    });
}

export const GTMAddToWishlistHandler = ({ curr, value, items }: { curr?: string, value?: number, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('add_to_wishlist', {}, {
        currency: curr || "",
        value: value || 0,
        items: items || [],
    });
}
export const GTMRemoveFromWishlistHandler = ({ curr, value, items }: { curr?: string, value?: number, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('remove_from_wishlist', {}, {
        currency: curr || "",
        value: value || 0,
        items: items || [],
    });
}

export const GTMViewWishlistHandler = ({ curr, value, items }: { curr?: string, value?: string, items?: IViewItemPayload[] }) => {
    return GTMEcommerceHandler('view_wishlist', {}, {
        currency: curr || "",
        value: value || '',
        items: items || [],
    });
}

export const GTMUserLogin = (uid: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'user_login',
        cf_uid: uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',

    });
    return 1;
}

export const GTMUserSignup = (uid: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'user_signup',
        cf_uid: uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',

    });
    return 1;
}

export const GTMUserProfile = (uid: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'profile',
        cf_uid: uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',

    });
    return 1;
}


export const GTMPurchaseFailedHandler = ({ curr, value, items, transaction_id, tax, shipping, cod_charge, coupon }: { curr?: string, value?: number, items?: IViewItemPayload[], transaction_id?: string, tax?: number, shipping?: number, cod_charge?: number, coupon?: string }) => {
    return GTMEcommerceHandler('order_failed', {}, {
        currency: curr || "",
        value: value || '',
        transaction_id: transaction_id || '',
        tax: tax || 0,
        shipping: shipping || 0,
        cod_charge: cod_charge || 0,
        items: items || [],
        coupon: coupon || '',
    });
}


export const GTMLanguageSwitch = (frm_lang?: string, to_lang?: string) => {
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: 'switch_language',
        cf_uid: user.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        lang_from: frm_lang || "",
        lang_to: to_lang || ""

    });
    return 1;
}

function GTMEcommerceHandler(event: string, parameters: Record<string, any> = {}, ecommerce: Record<string, any> = {}) {
    sendGTMEvent({ ecommerce: null });
    const { user, language, currency } = useCommonStore.getState();
    sendGTMEvent({
        event: event,
        cf_uid: user?.uid || '',
        cf_lang: language?.code || '',
        cf_currency: currency?.code || '',
        ...parameters,
        ecommerce: {
            ...ecommerce,
            currency: ecommerce?.currency || currency?.code || '',
        }
    });
    return 1;
}